import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, catchError, map, of, switchMap, timer } from 'rxjs';
import { ManagementService } from '../../management.service';
import { UtilService } from '../../../../services/util.service';
import { CustomValidators } from '../../../../shared/validators';
import { MetaPageRecord, MetaPageValidationResult } from '../../types';
import { TitledInputComponent } from '../../../../shared/components/titled-input/titled-input.component';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../../../shared/components/select-menu/select-menu.component';
import { ToggleSwitchComponent } from '../../../../shared/components/toggle-switch/toggle-switch.component';
import { DeleteBtnComponent } from '../../../../shared/components/delete-btn/delete-btn.component';

export type MetaPageFormGroup = FormGroup<{
  pageId: FormControl<string | null>;
  countries: FormControl<string[] | null>;
  isActive: FormControl<boolean | null>;
}>;

interface MetaValidationState {
  status: 'validating' | 'valid' | 'invalid' | 'error';
  pageName?: string | null;
  activeAdsCount?: number | null;
  message?: string;
}

@Component({
  selector: 'app-meta-pages',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TitledInputComponent,
    SelectMenuComponent,
    ToggleSwitchComponent,
    DeleteBtnComponent,
  ],
  templateUrl: './meta-pages.component.html',
  styleUrl: './meta-pages.component.scss',
})
export class MetaPagesComponent implements OnInit {
  // The FormArray is owned by the parent's form, so submit()/getBrandPayload keep operating on one
  // form tree (pending/validity propagate). This component only manages the rows, their validation
  // and the country options.
  @Input({ required: true }) array!: FormArray<MetaPageFormGroup>;
  // When set (edit mode), the brand's saved meta pages are loaded; otherwise a single empty row is seeded.
  @Input() existingBrandKey: string | null = null;

  public isMetaExpanded = true;
  public countryOptions: SelectItem[] = [];
  protected readonly SelectMode = SelectMode;

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private managementService = inject(ManagementService);
  private utilService = inject(UtilService);

  // Per-row SearchAPI validation display, keyed by the row's pageId control.
  private metaValidation = new Map<AbstractControl, MetaValidationState>();
  // Page id + countries combinations already confirmed valid (loaded from the server or just validated)
  // so we don't re-hit SearchAPI for unchanged values. Keyed by the row's pageId control.
  private trustedKeys = new Map<AbstractControl, string>();
  // SearchAPI results cached per page-id+countries key for this modal session, to avoid repeat (billed) calls.
  private validationCache = new Map<string, MetaPageValidationResult>();

  ngOnInit(): void {
    this.initCountryOptions();
    if (this.existingBrandKey) {
      this.loadMetaPages(this.existingBrandKey);
    } else {
      this.metaPages.push(this.createEmptyMetaRow());
    }
  }

  get metaPages(): FormArray<MetaPageFormGroup> {
    return this.array;
  }

  private loadMetaPages(brandKey: string): void {
    this.managementService.getBrandMetaPages(brandKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: pages => {
          pages.forEach(page => this.metaPages.push(this.createPrefilledMetaRow(page)));
          this.metaPages.push(this.createEmptyMetaRow());
        },
        error: () => {
          this.metaPages.push(this.createEmptyMetaRow());
        }
      });
  }

  // Countries come from a brand-scoped endpoint (READ_BRAND), independent of the dashboard's filter permissions.
  private initCountryOptions(): void {
    this.managementService.getMetaPageCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: countries => {
          this.countryOptions = countries.map(country => ({
            label: country.name,
            key: country.code,
            icon: this.utilService.getCountryFlagUrl(country.code)
          }));
        },
        error: () => { /* leave country options empty; the rest of the form still works */ }
      });
  }

  private createEmptyMetaRow(): MetaPageFormGroup {
    const row = this.fb.group({
      pageId: new FormControl<string | null>(null, {
        validators: [CustomValidators.numericOnly('Meta page ID')],
        asyncValidators: [this.validateMetaPageId()]
      }),
      countries: new FormControl<string[]>([]),
      isActive: new FormControl<boolean>(true),
    }, { validators: [this.metaRowValidator] }) as MetaPageFormGroup;

    // Validation depends on the selected countries (the request requires a non-empty array), so
    // re-run the page-id check whenever countries change — picking the first country triggers the
    // request, clearing them cancels it, and changing the set re-validates.
    const pageIdControl = row.get('pageId')!;
    row.get('countries')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => pageIdControl.updateValueAndValidity());

    return row;
  }

  private createPrefilledMetaRow(page: MetaPageRecord): MetaPageFormGroup {
    const row = this.createEmptyMetaRow();
    const pageIdControl = row.get('pageId')!;
    // Mark as trusted before setting the value so the async validator skips the SearchAPI call for unchanged pages.
    this.trustedKeys.set(pageIdControl, this.validationKey(page.page_id, page.countries));
    row.patchValue({ pageId: page.page_id, countries: page.countries, isActive: page.is_active });
    // Set the display AFTER patching: patchValue sets pageId before countries, so the page-id async
    // validator runs once while countries are still empty and clears metaValidation. The value is now
    // stable and trusted (no further async runs), so this restores the page name + ads count for good.
    this.metaValidation.set(pageIdControl, { status: 'valid', pageName: page.page_name, activeAdsCount: page.last_ads_count });
    return row;
  }

  private validateMetaPageId(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const pageId = (control.value ?? '').trim();
      const countries = (control.parent?.get('countries')?.value ?? []) as string[];
      if (!pageId) {
        this.metaValidation.delete(control);
        return of(null);
      }
      // The validate request requires a non-empty countries array, so don't send it until at least
      // one country is picked. The "select a country" requirement is enforced by metaRowValidator,
      // so returning null here doesn't let an unvalidated page slip through.
      if (countries.length === 0) {
        this.metaValidation.delete(control);
        return of(null);
      }
      const key = this.validationKey(pageId, countries);
      // Already confirmed (loaded from the server or just validated, value unchanged) — skip the billed call.
      if (this.trustedKeys.get(control) === key) {
        return of(null);
      }
      // Reuse a result already fetched for this page id + countries in this modal session.
      const cached = this.validationCache.get(key);
      if (cached) {
        return of(this.applyMetaValidation(control, key, cached));
      }
      this.metaValidation.set(control, { status: 'validating' });
      // Debounce: Angular cancels this observable when the value changes again, so we only hit
      // SearchAPI once the user pauses typing (not per keystroke).
      return timer(500).pipe(
        switchMap(() => this.managementService.validateMetaPage(pageId, countries)),
        map(result => {
          this.validationCache.set(key, result);
          return this.applyMetaValidation(control, key, result);
        }),
        catchError(() => {
          this.metaValidation.set(control, { status: 'error', message: 'Validation failed. Please try again.' });
          return of({ metaPageValidationFailed: true });
        })
      );
    };
  }

  // Stable key for a page-id + countries pair, used to cache validation results and skip redundant calls.
  private validationKey(pageId: string, countries: string[]): string {
    return `${pageId}|${[...countries].sort().join(',')}`;
  }

  private applyMetaValidation(control: AbstractControl, key: string, result: MetaPageValidationResult): ValidationErrors | null {
    if (!result.exists) {
      this.metaValidation.set(control, { status: 'invalid', message: 'This page was not found on Meta.' });
      return { metaPageNotFound: true };
    }
    this.trustedKeys.set(control, key);
    this.metaValidation.set(control, { status: 'valid', pageName: result.pageName, activeAdsCount: result.activeAdsCount });
    return null;
  }

  private metaRowValidator = (group: AbstractControl): ValidationErrors | null => {
    const pageId = (group.get('pageId')?.value ?? '').trim();
    const countries = group.get('countries')?.value ?? [];
    if (pageId && countries.length === 0) {
      return { countriesRequired: true };
    }
    return null;
  };

  getMetaPageIdControl(index: number): FormControl<string | null> {
    return this.metaPages.at(index).get('pageId') as FormControl<string | null>;
  }

  getMetaCountriesControl(index: number): FormControl<string[] | null> {
    return this.metaPages.at(index).get('countries') as FormControl<string[] | null>;
  }

  getMetaIsActiveControl(index: number): FormControl<boolean | null> {
    return this.metaPages.at(index).get('isActive') as FormControl<boolean | null>;
  }

  getMetaValidation(index: number): MetaValidationState | undefined {
    const pageIdControl = this.getMetaPageIdControl(index);
    // A non-numeric id fails the sync validator, so Angular never runs the async check — any stored
    // status is now stale. The format error is already shown under the input, so hide the leftover
    // status line instead of leaving "<old page> · N active ads" stranded between the two errors.
    if (pageIdControl.hasError('numericOnly')) {
      return undefined;
    }
    return this.metaValidation.get(pageIdControl);
  }

  isMetaRowCountriesMissing(index: number): boolean {
    return this.metaPages.at(index).hasError('countriesRequired');
  }

  isMetaRowEmpty(index: number): boolean {
    const row = this.metaPages.at(index);
    return !row.get('pageId')?.value && (row.get('countries')?.value?.length ?? 0) === 0;
  }

  toggleMeta(): void {
    this.isMetaExpanded = !this.isMetaExpanded;
  }

  removeMetaRow(index: number): void {
    const pageIdControl = this.getMetaPageIdControl(index);
    this.metaValidation.delete(pageIdControl);
    this.trustedKeys.delete(pageIdControl);
    this.metaPages.removeAt(index);
  }

  checkAndAddMetaRow(index: number): void {
    if (index === this.metaPages.length - 1) {
      const row = this.metaPages.at(index);
      if (row.get('pageId')?.value && (row.get('countries')?.value?.length ?? 0) > 0) {
        this.metaPages.push(this.createEmptyMetaRow());
      }
    }
  }
}
